import React from 'react';

import styled from '@emotion/styled';

import { default as CarouselNuka } from 'nuka-carousel';

import { useMediaQuery } from '@zego/hooks/useMediaQuery';
import { BREAKPOINTS, disabledColor, unit, unitTRBL } from '@zego/theme';

import CarouselTile from '@zego/components/molecules/CarouselTile';
import ContentContainer from '@zego/components/SCContentContainer';

interface CarouselProps {
  items: CarouselItems[];
  dotColourActive?: string;
  dotColourDisabled?: string;
  showOverflowOnMobile?: boolean;
}

interface CarouselItems {
  id: string;
  image: string;
  alt: string;
  title: string;
  description: string;
}

interface ContentContainerStyledProps {
  dotColourActive: string;
  dotColourDisabled: string;
  showOverflowOnMobile: boolean;
  numberOfColumns: number;
}

interface CarouselFunctionalityObject {
  dragging: boolean;
  slidesToShow: number;
}

const ContentContainerStyled = styled(
  ContentContainer,
)<ContentContainerStyledProps>`
.slider:focus, .slider:active, .slider-slide:focus {
  box-shadow: 0;
  outline: 0;
}

.slider-frame {
  overflow: ${(p): string =>
    p.showOverflowOnMobile ? 'visible !important' : 'hidden'}
}

.slider-control-bottomcenter > div {
  top: ${unit(7)}px;
}

.slider {
  margin: ${unitTRBL(3, 0, 3, 0)};
  @media screen and (min-width: 1024px) {
    margin: 0;
  }
}

.paging-dot {
  transform: scale(1.33);
}

.slider-control-bottomcenter {
  bottom: -50px !important;
  @media screen and (min-width: 1024px) {
    display: none;
  }
}

.slider-control-bottomcenter ul .paging-item {
  margin-left:${unit(1)}px;
  margin-right: ${unit(1)}px;
}

.slider-slide > div {
  margin: ${(p): string => (p.numberOfColumns <= 4 ? '0px 16px' : '0px 8px')}
}

${BREAKPOINTS.verySmallOnly} {
  .slider-control-bottomcenter ul .paging-item button {
    visibility: hidden;
  }
}
`;

export default function Carousel({
  items,
  dotColourActive = disabledColor(8),
  dotColourDisabled = disabledColor(6),
  showOverflowOnMobile = false,
}: CarouselProps): JSX.Element {
  const isMobile = useMediaQuery('(max-width: 512px)');
  const isTablet = useMediaQuery('(min-width:513px) and (max-width: 1023px)');

  function getCarouselFunctionality(): CarouselFunctionalityObject {
    if (isMobile) {
      return {
        dragging: true,
        slidesToShow: 1,
      };
    }

    if (isTablet) {
      return {
        dragging: true,
        slidesToShow: 2,
      };
    }

    return {
      dragging: false,
      slidesToShow: items.length,
    };
  }

  return (
    <ContentContainerStyled
      dotColourActive={dotColourActive}
      dotColourDisabled={dotColourDisabled}
      showOverflowOnMobile={showOverflowOnMobile}
      numberOfColumns={items.length}
    >
      <CarouselNuka
        initialSlideWidth={200}
        renderCenterLeftControls={(): null => null}
        renderCenterRightControls={(): null => null}
        edgeEasing="easePolyOut"
        cellAlign="left"
        {...getCarouselFunctionality()}
        defaultControlsConfig={{
          pagingDotsStyle: {
            fill: dotColourActive,
          },
        }}
      >
        {items.map(item => (
          <CarouselTile key={item.id} {...item} />
        ))}
      </CarouselNuka>
    </ContentContainerStyled>
  );
}
